// AlertsTile.js — recent llm_usage_alerts within the window.
// Counter at top; each alert is an expandable accordion row with full detail.

import React from 'react';
import {
  Card, CardContent, Typography, Box, Chip,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLLMAlerts } from '../../../hooks/useAdminMetrics';

const formatUSD = (n) => `$${Number(n ?? 0).toFixed(4)}`;

const formatCreatedAt = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toISOString().slice(0, 16).replace('T', ' ') + ' UTC';
};

const ALERT_LABELS = {
  per_user_daily_cost: 'Per-user daily cost',
  single_call_cost: 'Single-call cost',    // LLM aggregator
  single_leg_cost: 'Single-leg cost',      // Twilio aggregator
  system_cost_spike: 'System cost spike',
};

// Map `source` field (written by the aggregator) to a display chip. Alerts
// predating the source field (older LLM alerts) have no source → treated as
// 'llm' by default since the collection was originally LLM-only.
const SOURCE_CHIPS = {
  llm: { label: 'LLM', color: 'primary' },
  twilio: { label: 'Twilio', color: 'secondary' },
};

const AlertsTile = ({ days = 7 }) => {
  const { alerts, loading, error } = useLLMAlerts(days);

  if (error) {
    return (
      <Card><CardContent>
        <Typography color="error">Alerts unavailable: {error.message}</Typography>
      </CardContent></Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="h6">Open Cost Alerts ({days}d)</Typography>
          <Chip
            label={loading ? '…' : alerts.length}
            color={alerts.length > 0 ? 'warning' : 'default'}
            size="small"
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Anomalies flagged by automatic thresholds: per-user daily cost spikes, single-call/leg cost spikes, and system-wide cost spikes.
        </Typography>
        {loading ? (
          <Typography variant="body2" color="text.secondary">Loading…</Typography>
        ) : alerts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No open alerts in the last {days} days.</Typography>
        ) : (
          <Box>
            {alerts.map((a) => (
              <Accordion key={a.id} disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    {(() => {
                      const src = SOURCE_CHIPS[a.source || 'llm'];
                      return <Chip label={src.label} size="small" color={src.color} />;
                    })()}
                    <Chip label={ALERT_LABELS[a.type] || a.type} size="small" variant="outlined" />
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      {a.date} — {formatUSD(a.cost_usd)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCreatedAt(a.created_at)}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">type</Typography>
                    <Typography variant="body2">{a.type}</Typography>
                    <Typography variant="caption" color="text.secondary">date</Typography>
                    <Typography variant="body2">{a.date}</Typography>
                    {a.uid && (<>
                      <Typography variant="caption" color="text.secondary">uid</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{a.uid}</Typography>
                    </>)}
                    <Typography variant="caption" color="text.secondary">cost_usd</Typography>
                    <Typography variant="body2">{formatUSD(a.cost_usd)}</Typography>
                    <Typography variant="caption" color="text.secondary">threshold</Typography>
                    <Typography variant="body2">{formatUSD(a.threshold)}</Typography>
                    {a.call_sid && (<>
                      <Typography variant="caption" color="text.secondary">call_sid</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{a.call_sid}</Typography>
                    </>)}
                    {a.function && (<>
                      <Typography variant="caption" color="text.secondary">function</Typography>
                      <Typography variant="body2">{a.function}</Typography>
                    </>)}
                    {a.model && (<>
                      <Typography variant="caption" color="text.secondary">model</Typography>
                      <Typography variant="body2">{a.model}</Typography>
                    </>)}
                    {a.moving_average_usd != null && (<>
                      <Typography variant="caption" color="text.secondary">moving_avg</Typography>
                      <Typography variant="body2">{formatUSD(a.moving_average_usd)}</Typography>
                    </>)}
                    {a.multiplier != null && (<>
                      <Typography variant="caption" color="text.secondary">multiplier</Typography>
                      <Typography variant="body2">{a.multiplier}×</Typography>
                    </>)}
                    <Typography variant="caption" color="text.secondary">details</Typography>
                    <Typography variant="body2">{a.details}</Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default AlertsTile;
